import { useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Truck, CheckCircle, Warning, UploadSimple } from "@phosphor-icons/react";
import { fmtNumber } from "@/lib/format";

const TEMPLATE_CSV = "Chasis Number,Engine Number,BranchName\nMD638CS18P1K15149,BS1HP1100003,MAIN ROAD KAKINADA\n";

export default function BulkMoveVehiclesPanel() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showAllErrors, setShowAllErrors] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setShowAllErrors(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(null);
    setResult(null);
    setShowAllErrors(false);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "move_branches_template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const runPreview = async () => {
    if (!file) {
      toast.error("Choose a CSV file first.");
      return;
    }
    setPreviewing(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/vehicles/admin/bulk-move-by-vin/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      if (data.planned === 0) {
        toast.warning("No moves will happen — check the errors list.");
      } else {
        toast.success(`Preview ready: ${data.planned} vehicles will move.`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    if (!file || !preview || preview.planned === 0) return;
    if (!window.confirm(`Move ${preview.planned} vehicles to their target branches now?`)) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/vehicles/admin/bulk-move-by-vin/commit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setPreview(null);
      toast.success(`Moved ${data.moved} vehicles · ${data.audit_entries} transfer log(s) created`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  const errors = preview?.errors || [];
  const visibleErrors = showAllErrors ? errors : errors.slice(0, 10);

  return (
    <div className="border-2 border-ink" data-testid="bulk-move-vehicles-panel">
      <div className="px-4 py-3 border-b border-canvas-line flex items-center gap-2">
        <Truck size={18} weight="bold" className="text-ink" />
        <h3 className="font-display text-base font-semibold">
          Bulk Move Vehicles by VIN → Branch
        </h3>
      </div>

      <div className="p-4 space-y-4 text-sm">
        <div className="text-ink-muted">
          Upload a CSV with <code className="font-mono text-[11px] px-1 py-0.5 bg-canvas-panel border border-canvas-line">Chasis Number, Engine Number, BranchName</code>. We&apos;ll find each VIN and move it to the named branch.
          Already-sold vehicles are skipped automatically.
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={downloadTemplate}
            data-testid="bulkmove-template-btn"
            className="px-3 py-1.5 border border-canvas-line text-xs font-mono uppercase tracking-wider text-ink hover:border-ink"
          >
            Download Template
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onFile}
            data-testid="bulkmove-file-input"
            className="text-xs"
          />
          {file && (
            <span className="font-mono text-[11px] text-ink-muted truncate max-w-[260px]">
              {file.name}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={runPreview}
            disabled={!file || previewing}
            data-testid="bulkmove-preview-btn"
            className="px-3 py-2 border border-ink text-ink text-xs font-semibold hover:bg-ink hover:text-white disabled:opacity-50 inline-flex items-center gap-2"
          >
            <UploadSimple size={13} weight="bold" />
            {previewing ? "Analysing…" : "Preview Moves (Dry-Run)"}
          </button>
          <button
            type="button"
            onClick={commit}
            disabled={!preview || preview.planned === 0 || committing}
            data-testid="bulkmove-commit-btn"
            className="px-3 py-2 bg-ink text-white text-xs font-semibold hover:bg-brand disabled:opacity-50"
            title={!preview ? "Run preview first" : "Commit the bulk move"}
          >
            {committing ? "Moving…" : `Apply Move${preview ? ` (${preview.planned})` : ""}`}
          </button>
          {(preview || result) && (
            <button
              type="button"
              onClick={reset}
              className="px-3 py-2 border border-canvas-line text-ink-muted text-xs hover:border-ink hover:text-ink"
            >
              Reset
            </button>
          )}
        </div>

        {/* Preview block */}
        {preview && (
          <div className="border border-ink bg-canvas-card p-3 space-y-3" data-testid="bulkmove-preview-result">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="border border-canvas-line p-2">
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Rows Read</div>
                <div className="font-display text-xl font-bold tabular-nums">{fmtNumber(preview.total_rows)}</div>
              </div>
              <div className="border border-[#00B341] p-2">
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Will Move</div>
                <div className="font-display text-xl font-bold tabular-nums text-[#00713a]">{fmtNumber(preview.planned)}</div>
              </div>
              <div className="border border-destructive p-2">
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Errors / Skipped</div>
                <div className="font-display text-xl font-bold tabular-nums text-destructive">{fmtNumber(errors.length)}</div>
              </div>
            </div>

            {preview.by_branch?.length > 0 && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">By Target Branch</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs">
                  {preview.by_branch.map((b) => (
                    <div
                      key={b.branch_id}
                      className="border border-canvas-line px-2 py-1 bg-canvas flex items-center justify-between"
                      data-testid={`bulkmove-target-${b.branch_id}`}
                    >
                      <span className="font-mono text-[10px] text-ink-muted truncate">{b.branch_name}</span>
                      <span className="font-mono tabular-nums font-semibold">{b.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {errors.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted inline-flex items-center gap-1">
                    <Warning size={12} weight="bold" /> Skipped rows
                  </div>
                  {errors.length > 10 && (
                    <button
                      type="button"
                      onClick={() => setShowAllErrors((s) => !s)}
                      className="font-mono text-[10px] uppercase tracking-wider text-ink-muted hover:text-ink"
                    >
                      {showAllErrors ? "Show first 10" : `Show all ${errors.length}`}
                    </button>
                  )}
                </div>
                <div className="border border-canvas-line max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-canvas-panel sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Row</th>
                        <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">VIN</th>
                        <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Branch</th>
                        <th className="text-left px-2 py-1 font-mono text-[10px] uppercase text-ink-muted">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleErrors.map((er, i) => (
                        <tr key={i} className="border-t border-canvas-line">
                          <td className="px-2 py-1 font-mono text-[10px] text-ink-muted">{er.row_no}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{er.vin || "—"}</td>
                          <td className="px-2 py-1 text-[11px]">{er.branch_name || "—"}</td>
                          <td className="px-2 py-1 text-[11px] text-destructive">{er.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {result && (
          <div
            className="border border-[#00B341] bg-[#00B341]/5 p-3 flex items-center gap-2"
            data-testid="bulkmove-result"
          >
            <CheckCircle size={16} weight="bold" className="text-[#00B341]" />
            <span>
              <strong>{fmtNumber(result.moved)}</strong> vehicles moved · {result.audit_entries} transfer log entry(ies) written.
              View them under Inventory → Transfer Log.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
